import React, {useState, useEffect, useMemo} from "react";
import Avatar from "react-avatar";
import {LABEL_COLORS} from "constants/StyleConstants.js";

interface Props {
  appUser?: any;
  size?: string | number;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: any) => void;
  title?: string;
  alt?: string;
  [key: string]: any;
}

function UserAvatar({appUser, size = "80", className, style, onClick, title, alt, ...props}: Props) {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageError, setImageError] = useState(false);
  const avatarClassName = ["rs-user-avatar", className].filter(Boolean).join(" ");
  const imageClassName = ["rs-user-avatar__image", className].filter(Boolean).join(" ");
  const nameLabel = appUser?.displayName || appUser?.name || "User";

  useEffect(() => {
    if (appUser?.imgSrc) {
      setImageSrc(appUser.imgSrc);
      setImageError(false);
    } else {
      setImageSrc(null);
      setImageError(false);
    }
  }, [appUser?.imgSrc]);

  const handleImageError = () => {
    console.log("UserAvatar - Image failed to load, falling back to initials");
    setImageError(true);
  };

  const handleImageLoad = () => {
    console.log("UserAvatar - Image loaded successfully");
    setImageError(false);
  };

  const sizeValue = useMemo(() => {
    if (typeof size === "number") {
      return `${size}px`;
    }
    if (typeof size === "string" && /^\d+$/.test(size)) {
      return `${size}px`;
    }
    return size;
  }, [size]);

  const imageStyle: React.CSSProperties = useMemo(
    () => ({
      width: sizeValue,
      height: sizeValue,
      borderRadius: "50%",
      objectFit: "cover",
      objectPosition: "center",
      display: "block",
      ...style
    }),
    [sizeValue, style]
  );

  if (imageSrc && !imageError) {
    return (
      <img
        className={imageClassName}
        src={imageSrc}
        alt={alt || nameLabel}
        title={title || nameLabel}
        onClick={onClick}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={imageStyle}
      />
    );
  }

  return (
    <Avatar
      className={avatarClassName}
      name={nameLabel}
      size={size as any}
      fgColor="white"
      color={`${LABEL_COLORS.primary}`}
      round
      onClick={onClick}
      title={title}
      alt={alt}
      style={style}
      {...props}
    />
  );
}

export default UserAvatar;
